import React from 'react';
import styled from 'styled-components';
import { TeacherWorkScheduleDTO, WEEKDAY_LABELS } from '../../../types/workingSchedule';
import { isDayEnabled, setDay, isValidWindow } from '../../../utils/workingSchedule';

const FormWrap = styled.div`
  display: grid; gap: .5rem;
`;
const Row = styled.div`
  display: grid; gap: .35rem;
`;
const DaysGrid = styled.div`
  display: grid; grid-template-columns: repeat(7, minmax(0,1fr)); gap: .25rem;
  @media (max-width: 640px){ grid-template-columns: repeat(4, minmax(0,1fr)); }
`;
const DayBtn = styled.button<{active?:boolean}>`
  border: 1px solid var(--card-border, rgba(0,0,0,0.1));
  border-radius: 8px; padding: .4rem .25rem; font-weight: 800; cursor: pointer;
  font-size: 0.75rem;
  background: ${p=>p.active?'linear-gradient(135deg,#667eea,#764ba2)':'#fff'};
  color: ${p=>p.active?'#fff':'var(--text,#333)'};
`;
const Inputs = styled.div`
  display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: .5rem;
  @media (max-width: 640px){ grid-template-columns: 1fr; }
`;
const TimeInput = styled.input`
  border: 1px solid var(--card-border, rgba(0,0,0,0.1)); border-radius: 8px; padding: .4rem .5rem;
  font-size: 0.85rem;
`;
const Actions = styled.div`
  display: flex; gap: .5rem; align-items: center; justify-content: flex-end;
  margin-top: 0.25rem;
`;
const SaveBtn = styled.button<{disabled?:boolean}>`
  background: ${p=>p.disabled?'#cbd5e1':'linear-gradient(135deg, #667eea, #764ba2)'}; color: #fff; border: 0; border-radius: 8px; padding: .4rem .8rem; font-weight: 900; cursor: ${p=>p.disabled?'not-allowed':'pointer'};
  font-size: 0.85rem;
`;
const CancelBtn = styled.button`
  background: transparent; color: var(--text,#333); border: 1px solid var(--card-border, rgba(0,0,0,0.1)); border-radius: 8px; padding: .4rem .8rem; font-weight: 900; cursor: pointer;
  font-size: 0.85rem;
`;
const Error = styled.div`
  color: #b00020; font-weight: 800; font-size: .8rem;
`;
const Note = styled.div`
  color: var(--text-light,#556); font-size: .8rem;
`;

export type WorkingScheduleFormProps = {
  value: TeacherWorkScheduleDTO,
  onSubmit: (v: TeacherWorkScheduleDTO) => Promise<void> | void,
  onChange?: (v: TeacherWorkScheduleDTO) => void,
  loading?: boolean,
  error?: string | null,
  readOnly?: boolean,
  onCancel?: ()=>void,
  helperText?: string,
  showSaveButton?: boolean,
  noForm?: boolean,
  // New props for maxLessonsPerStudent
  maxLessonsPerStudent?: number,
  onMaxLessonsChange?: (val: number) => void,
};

const WorkingScheduleForm: React.FC<WorkingScheduleFormProps> = ({ 
  value, 
  onSubmit, 
  onChange, 
  loading, 
  error, 
  readOnly, 
  onCancel, 
  helperText,
  showSaveButton = true,
  noForm = false,
  maxLessonsPerStudent = 3,
  onMaxLessonsChange,
}) => {
  const [local, setLocal] = React.useState<TeacherWorkScheduleDTO>(value);
  React.useEffect(()=>{ setLocal(value); }, [value?.teacherId, value?.workingDaysMask, value?.startTime, value?.endTime]);

  const update = (patch: Partial<TeacherWorkScheduleDTO>) => {
    const next = { ...local, ...patch } as TeacherWorkScheduleDTO;
    setLocal(next);
    onChange && onChange(next);
  };

  const toggle = (idx: number) => {
    if (readOnly) return;
    const mask = setDay(local.workingDaysMask ?? 0, idx, !isDayEnabled(local.workingDaysMask ?? 0, idx));
    update({ workingDaysMask: mask });
  };

  const validMask = Number.isFinite(local.workingDaysMask) && (local.workingDaysMask ?? 0) !== 0;
  const validWindow = isValidWindow(local.startTime, local.endTime);
  const canSave = validMask && validWindow && !loading && !readOnly;

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!canSave) return;
    await onSubmit(local);
  };

  const Content = (
    <FormWrap>
      {helperText && <Note>{helperText}</Note>}
      <Row>
        <div style={{ fontWeight: 900 }}>Working days</div>
        <DaysGrid>
          {WEEKDAY_LABELS.map((lbl, i)=> (
            <DayBtn key={i} type="button" active={isDayEnabled(local.workingDaysMask ?? 0, i)} onClick={()=>toggle(i)} disabled={!!readOnly}>{lbl}</DayBtn>
          ))}
        </DaysGrid>
      </Row>
      <Row>
        <div style={{ fontWeight: 900 }}>Working hours</div>
        <Inputs>
          <label style={{ display:'grid', gap:4 }}>
            <span style={{ fontWeight:700, fontSize:12, color:'var(--text-light,#666)' }}>Start</span>
            <TimeInput type="time" value={local.startTime || ''} onChange={(e)=> update({ startTime: e.target.value })} disabled={!!readOnly} step={60} />
          </label>
          <label style={{ display:'grid', gap:4 }}>
            <span style={{ fontWeight:700, fontSize:12, color:'var(--text-light,#666)' }}>End</span>
            <TimeInput type="time" value={local.endTime || ''} onChange={(e)=> update({ endTime: e.target.value })} disabled={!!readOnly} step={60} />
          </label>
          <label style={{ display:'grid', gap:4 }}>
            <span style={{ fontWeight:700, fontSize:12, color:'var(--text-light,#666)' }}>Max lessons/student</span>
            <TimeInput 
              type="number" 
              value={maxLessonsPerStudent} 
              onChange={(e)=> onMaxLessonsChange && onMaxLessonsChange(Number(e.target.value))} 
              disabled={!!readOnly} 
              min={1}
            />
          </label>
        </Inputs>
        {!validWindow && <Error>Start time must be before end time (HH:mm)</Error>}
        {!validMask && <Error>At least one working day must be selected</Error>}
        {error && <Error>⚠️ {error}</Error>}
      </Row>
      <Actions>
        {onCancel && <CancelBtn type="button" onClick={()=>onCancel?.()}>Cancel</CancelBtn>}
        {showSaveButton && !readOnly && <SaveBtn type="submit" disabled={!canSave}>{loading? 'Saving...' : 'Save'}</SaveBtn>}
      </Actions>
    </FormWrap>
  );

  if (noForm) return Content;

  return (
    <form onSubmit={handleSubmit}>
      {Content}
    </form>
  );
};

export default WorkingScheduleForm;
